/**
 * F14.2 — CADUCIDAD DE LOS DATOS DESCARGADOS (común a todos los roles)
 *
 * Función pura, sin IndexedDB ni red. Sustituye al corte único de 24 h de FASE 5 por tres tramos, medidos desde
 * que el servidor generó el paquete (`generadoEn`):
 *
 *   VIGENTE    0–24 h   se trabaja con normalidad.
 *   AVISO     24–72 h   se trabaja, con el aviso «Los datos descargados pueden estar desactualizados».
 *   CADUCADO   ≥ 72 h   NO se empieza trabajo NUEVO sólo con estos datos. Sí se puede terminar un trabajo ya
 *                       iniciado, guardar sus acciones, consultar todo y conservar lo pendiente.
 *
 * Nunca se borra nada pendiente por caducidad: esto sólo decide si se deja REGISTRAR una acción nueva. Con
 * conexión, el paquete se actualiza antes de seguir (lo hace quien muestra el trabajo, no este módulo).
 *
 * «TRABAJO INICIADO» — la unidad es la LÍNEA (tarea), nunca la OT (determinista, persistente, no depende de la
 * pantalla). Una línea está iniciada en ESTE dispositivo si, según lo guardado en él,
 *   · SU estado en el paquete ya no es PENDIENTE (el servidor la tenía en marcha al descargar), o
 *   · hay alguna operación local SOBRE ESA LÍNEA en la cola (acción o foto; cualquier estado: pendiente, con error,
 *     en revisión, rechazada o ya guardada en el servidor y aún en el historial local).
 * Lo de OTRA línea no cuenta: haber trabajado A (en local o en el servidor) nunca habilita empezar B de la misma OT
 * con datos de más de 72 h. Las dos fuentes están en IndexedDB (cifradas): sobreviven a recargas, a cerrar la
 * aplicación y a cambiar de pantalla, y no se pueden «fabricar» abriendo una OT ni navegando. Con más de 72 h no se
 * puede crear una operación sobre una línea no iniciada, así que una línea sólo puede estar iniciada desde ANTES de
 * caducar (o por el servidor). El reloj sólo decide el tramo, nunca si una línea está iniciada.
 *
 * Operaciones que no son de una línea (ver `mutaciones.ts::comprobarCaducidad`): una NC ya existente en el dispositivo
 * (sus transiciones y fotos) es trabajo en curso; una NC nueva sobre líneas exige que TODAS estén iniciadas; lo de
 * ámbito OT (foto de la OT, NC de toda la OT) exige alguna línea iniciada — y nada de eso inicia ninguna línea.
 *
 * Los umbrales los fija el servidor en el paquete (`validoOperativamenteHasta` = +24 h,
 * `bloqueaTrabajoNuevoDesde` = +72 h); un paquete anterior a F14.2 (sin el segundo) usa `generadoEn` + 72 h.
 */

export type NivelCaducidad = 'VIGENTE' | 'AVISO' | 'CADUCADO';

export const HORAS_BLOQUEO_TRABAJO_NUEVO = 72;

export const AVISO_DATOS_DESACTUALIZADOS = 'Los datos descargados pueden estar desactualizados';

export const MOTIVO_TRABAJO_NUEVO_BLOQUEADO =
  'Los datos de este dispositivo tienen más de 72 h. Puedes terminar las tareas que ya empezaste; para empezar una tarea nueva, conéctate y actualiza los datos.';

export interface UmbralesCaducidad {
  readonly generadoEn: string;
  readonly validoOperativamenteHasta: string;
  readonly bloqueaTrabajoNuevoDesde?: string | null;
}

export function instanteBloqueo(meta: UmbralesCaducidad): number {
  if (meta.bloqueaTrabajoNuevoDesde != null) return new Date(meta.bloqueaTrabajoNuevoDesde).getTime();
  return new Date(meta.generadoEn).getTime() + HORAS_BLOQUEO_TRABAJO_NUEVO * 36e5;
}

export function nivelCaducidad(meta: UmbralesCaducidad, ahora: number = Date.now()): NivelCaducidad {
  if (ahora >= instanteBloqueo(meta)) return 'CADUCADO';
  if (ahora >= new Date(meta.validoOperativamenteHasta).getTime()) return 'AVISO';
  return 'VIGENTE';
}

/** Ver la cabecera: la línea del paquete local (su propio estado) + las operaciones locales de ESA línea. */
export function lineaIniciadaEnDispositivo(
  linea: { readonly estado: string } | null,
  operacionesDeLaLinea: readonly unknown[],
): boolean {
  return operacionesDeLaLinea.length > 0 || (linea !== null && linea.estado !== 'PENDIENTE');
}

export type DecisionCaducidad =
  | { readonly permitido: true; readonly aviso: string | null }
  | { readonly permitido: false; readonly motivo: string };

/** ¿Se puede registrar una acción nueva sobre esta unidad de trabajo (línea, NC…) con los datos locales? */
export function decidirPorCaducidad(nivel: NivelCaducidad, iniciada: boolean): DecisionCaducidad {
  if (nivel === 'VIGENTE') return { permitido: true, aviso: null };
  if (nivel === 'AVISO') return { permitido: true, aviso: AVISO_DATOS_DESACTUALIZADOS };
  return iniciada ? { permitido: true, aviso: AVISO_DATOS_DESACTUALIZADOS } : { permitido: false, motivo: MOTIVO_TRABAJO_NUEVO_BLOQUEADO };
}

/** «hace 3 h», «hace 2 días»: cuándo se descargaron los datos, en palabras. */
export function antiguedadDatos(generadoEn: string, ahora: number = Date.now()): string {
  const minutos = Math.max(0, Math.floor((ahora - new Date(generadoEn).getTime()) / 60_000));
  if (minutos < 1) return 'ahora mismo';
  if (minutos < 60) return `hace ${String(minutos)} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 48) return `hace ${String(horas)} h`;
  return `hace ${String(Math.floor(horas / 24))} días`;
}
